import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.heat';
import { TranslateService } from '@ngx-translate/core';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';
import { ResidentPoint, CardPoint } from '../../../../services/user-analytics/user-analytics.model';

@Component({
    selector: 'app-user-heatmap',
    templateUrl: './user-heatmap.component.html',
    styleUrls: ['./user-heatmap.component.scss'],
})
export class UserHeatmapComponent implements OnChanges, OnDestroy {
    @Input() residents: ResidentPoint[] = [];
    @Input() cards: CardPoint[] = [];

    private map: L.Map;
    private heatLayer: any;
    private mapReady = false;
    private defaultCenter: L.LatLngExpression = [45.5017, -73.5673];
    private defaultZoom = 13;

    selectedLayer: 'residents' | 'cards' = 'residents';

    constructor(private translate: TranslateService) {}

    get activePoints(): { lat: number; lng: number }[] {
        if (this.selectedLayer === 'residents') {
            return this.residents.map((p) => ({ lat: p.latitude, lng: p.longitude }));
        }
        return this.cards.map((p) => ({ lat: p.latitude, lng: p.longitude }));
    }

    get layerTabs(): { value: 'residents' | 'cards'; label: string }[] {
        return [
            { value: 'residents', label: this.translate.instant('user_records.analytics.heatmap_residents') },
            { value: 'cards', label: this.translate.instant('user_records.analytics.heatmap_cards') },
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        const hasData = this.residents?.length > 0 || this.cards?.length > 0;
        if (hasData && !this.mapReady) {
            setTimeout(() => this.initMap(), 100);
        } else if (this.mapReady && (changes.residents || changes.cards)) {
            this.updateHeatmap();
        }
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    onLayerChange(layer: 'residents' | 'cards'): void {
        this.selectedLayer = layer;
        this.updateHeatmap();
    }

    private initMap(): void {
        const container = document.getElementById('user-analytics-heatmap');
        if (!container || container.clientHeight === 0) {
            setTimeout(() => this.initMap(), 200);
            return;
        }

        if (this.map) {
            this.map.remove();
        }

        this.map = L.map('user-analytics-heatmap', {
            zoomControl: false,
        }).setView(this.defaultCenter, this.defaultZoom);

        L.control.zoom({ position: 'topright' }).addTo(this.map);

        L.tileLayer(MAP_TILE_URL, {
            maxZoom: 18,
            minZoom: 3,
            attribution: MAP_TILE_ATTRIBUTION,
        }).addTo(this.map);

        this.mapReady = true;
        this.updateHeatmap();
    }

    private updateHeatmap(): void {
        if (!this.map) return;

        if (this.heatLayer) {
            this.map.removeLayer(this.heatLayer);
            this.heatLayer = null;
        }

        const pts = this.activePoints;
        if (pts.length === 0) return;

        const heatData: L.HeatLatLngTuple[] = pts.map((p) => [p.lat, p.lng, 1]);

        this.heatLayer = (L as any)
            .heatLayer(heatData, {
                radius: 25,
                blur: 15,
                maxZoom: 17,
                max: 1,
                gradient: { 0.4: '#06b6d4', 0.65: '#f59e0b', 1: '#ef4444' },
            })
            .addTo(this.map);

        const bounds = L.latLngBounds(pts.map((p) => [p.lat, p.lng] as L.LatLngTuple));
        this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }
}
